import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { openFeature as openFeatureAction, IOpenFeatureParams } from 'shared/map-search/actions/features';
import { useApplicationContext } from 'shared/map-search/containers/ApplicationContext';
import { MAP_SET_CENTER_OPTIONS } from 'shared/map-search/containers/Map/constants';
import { useMapContext } from 'shared/map-search/containers/Map/context';
import { selectClustersType } from 'shared/map-search/selectors/clustersType';
import { selectSource } from 'shared/map-search/selectors/source';
import { EClustersType } from 'shared/map-search/types/clustersType';
import { IHotspotProperties, TFeature } from 'shared/map-search/types/map';
import { TMapBounds } from 'shared/map-search/types/mapBounds';
import { IApplicationState, TThunkDispatch } from 'shared/map-search/types/redux';
import { zoomToPrecision, precisionToMinZoom } from 'shared/map-search/utils/precision';

import { trackPinClick, trackPromoPinClick } from './tracking';
import { useFeaturesContext } from '../context';

interface IClickHandlersProps {
  resultsAvailable: boolean;
  detailsVisible: boolean;
  promoDetailsVisible: boolean;
}

/** клики по пинам на карте */
export const ClickHandlers: React.FC<IClickHandlersProps> = ({
  resultsAvailable: isResultsAvailable,
  detailsVisible: isDetailsVisible,
  promoDetailsVisible: isPromoDetailsVisible,
}) => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const { map } = useMapContext();
  const { precision } = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  const isResidentialComplexesListing = useSelector<IApplicationState, boolean>(state => state.isNewbuildingsListing);
  const { hotspotsLayer, balloonManager, balloonPromoManager } = useFeaturesContext();
  const clustersType = useSelector(selectClustersType);
  const source = useSelector(selectSource);

  const openFeature = React.useCallback(
    (params: Omit<IOpenFeatureParams, 'deviceType'>) => dispatch(openFeatureAction({ ...params, deviceType, source })),
    [dispatch, deviceType, source],
  );

  const handleHotspotClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const currentZoom = map.getZoom();
      const [, maxPossibleZoom] = map.zoomRange.getCurrent();
      const activeObject = event.get<YMaps.IHotspotLayerObject<IHotspotProperties>>('activeObject');
      const { originalFeature } = activeObject.getProperties();

      setTimeout(
        () =>
          trackPinClick({
            isDetailsVisible,
            isExtended: !!originalFeature.properties.isExtended,
            isResidentialComplex: originalFeature.properties.hasNewobject,
            hasNewbuildingsOnly: !!originalFeature.properties.hasNewbuildingsOnly,
            isResidentialComplexesListing,
            zoom: Math.round(currentZoom),
            newbuilding: originalFeature.properties.newbuilding,
            isSuburbanFromKp: !!originalFeature.properties.isAnyFromKp,
            hasDiscountOffers: !!originalFeature.properties.hasDiscountOffers,
            isMainNewbuildingPin: !!originalFeature.properties.isNewbuildingMainPin,
          }),
        0,
      );

      if (
        (!isResultsAvailable && currentZoom < maxPossibleZoom) ||
        (clustersType === EClustersType.Peas && !originalFeature.properties.favoriteIds?.length)
      ) {
        const currentPrecision = zoomToPrecision(config, currentZoom);
        const nextPrecision = currentPrecision + 1;
        const nextPrecisionZoom = precisionToMinZoom(config, nextPrecision);
        const nextFallbackZoom = currentZoom + 2;
        const coords = originalFeature.geometry.coordinates;

        const nextZoom = Math.min(nextPrecisionZoom || nextFallbackZoom, maxPossibleZoom);
        const center: YMaps.TCoord = [coords[0], coords[1]];

        map.setCenter(center, nextZoom, MAP_SET_CENTER_OPTIONS);

        return;
      }

      openFeature({ feature: originalFeature, precision, isDetailed: isDetailsVisible });
    },
    [
      map,
      isDetailsVisible,
      isResidentialComplexesListing,
      isResultsAvailable,
      clustersType,
      openFeature,
      precision,
      config,
    ],
  );

  const handleBalloonClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const feature = balloonManager.objectManager.objects.getById<TFeature>(objectId);
      const currentZoom = map.getZoom();

      trackPinClick({
        isDetailsVisible,
        isExtended: !!feature.properties.isExtended,
        isResidentialComplex: feature.properties.hasNewobject,
        hasNewbuildingsOnly: !!feature.properties.hasNewbuildingsOnly,
        isResidentialComplexesListing,
        zoom: Math.round(currentZoom),
        newbuilding: feature.properties.newbuilding,
        isSuburbanFromKp: !!feature.properties.isAnyFromKp,
        hasDiscountOffers: !!feature.properties.hasDiscountOffers,
        isMainNewbuildingPin: !!feature.properties.isNewbuildingMainPin,
      });

      openFeature({ feature, precision, isDetailed: isDetailsVisible });
    },
    [balloonManager, map, isResidentialComplexesListing, openFeature, precision, isDetailsVisible],
  );

  /**
   * @todo разработка в MNZ-700
   * Обработчик клика по пинам в промо-слое
   */
  const handleBalloonPromoClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const feature = balloonPromoManager.objectManager.objects.getById<TFeature>(objectId);
      const currentZoom = map.getZoom();

      trackPromoPinClick({
        isDetailsVisible,
        isExtended: !!feature.properties.isExtended,
        isResidentialComplex: feature.properties.hasNewobject,
        hasNewbuildingsOnly: !!feature.properties.hasNewbuildingsOnly,
        isResidentialComplexesListing,
        zoom: Math.round(currentZoom),
        newbuilding: feature.properties.newbuilding,
        isSuburbanFromKp: !!feature.properties.isAnyFromKp,
        hasDiscountOffers: !!feature.properties.hasDiscountOffers,
        isMainNewbuildingPin: !!feature.properties.isNewbuildingMainPin,
      });

      openFeature({ feature, precision, isDetailed: isPromoDetailsVisible });
    },
    [balloonPromoManager, map, isResidentialComplexesListing, openFeature, precision, isPromoDetailsVisible],
  );

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('click', handleHotspotClick);
    balloonManager.objectManager.objects.events.add('click', handleBalloonClick);
    balloonPromoManager.objectManager.objects.events.add('click', handleBalloonPromoClick);

    return () => {
      hotspotsLayer.layer.events.remove('click', handleHotspotClick);
      balloonManager.objectManager.objects.events.remove('click', handleBalloonClick);
      balloonPromoManager.objectManager.objects.events.remove('click', handleBalloonPromoClick);
    };
  }, [
    balloonManager,
    balloonPromoManager,
    handleBalloonClick,
    handleHotspotClick,
    hotspotsLayer,
    handleBalloonPromoClick,
  ]);

  return null;
};
